/**
 * Reports Service
 * Service para consumir API de relatórios
 */

import { apiClient } from './api-client';

export interface ReportDefinition {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  reportType: string;
  parameters: ReportParameter[];
  columns: ReportColumn[];
  isPublic: boolean;
  requiredPermissions: string[];
  tags: string[];
}

export interface ReportParameter {
  id: string;
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect';
  label: string;
  description?: string;
  required: boolean;
  defaultValue?: string | number | boolean | null;
  options?: { value: string | number | boolean; label: string }[];
}

export interface ReportColumn {
  field: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'currency' | 'percentage' | 'boolean';
  width?: number;
}

export interface ReportResult {
  executionId: string;
  reportCode: string;
  data: Array<Record<string, unknown>>;
  aggregations?: Record<string, unknown>;
  metadata: {
    executionTime: number;
    totalRecords: number;
    parameters: Record<string, unknown>;
    generatedAt: string;
    fromCache: boolean;
  };
}

export interface GenerateReportRequest {
  reportCode: string;
  parameters?: Record<string, unknown>;
  filters?: Array<Record<string, unknown>>;
}

export interface ExportReportRequest extends GenerateReportRequest {
  format: 'csv' | 'json' | 'pdf' | 'excel';
}

class ReportsService {
  /**
   * Listar relatórios disponíveis
   */
  async listReports(category?: string): Promise<ReportDefinition[]> {
    const params = category ? { category } : {};
    const response = await apiClient.get('/api/reports', { params });
    return response.data.data;
  }

  /**
   * Obter definição de relatório
   */
  async getReportDefinition(code: string): Promise<ReportDefinition> {
    const response = await apiClient.get(`/api/reports/${code}`);
    return response.data.data;
  }

  /**
   * Gerar relatório
   */
  async generateReport(request: GenerateReportRequest): Promise<ReportResult> {
    const response = await apiClient.post('/api/reports/generate', request);
    return response.data.data;
  }

  /**
   * Exportar relatório
   */
  async exportReport(request: ExportReportRequest): Promise<Blob> {
    const response = await apiClient.post('/api/reports/export', request, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Download de arquivo exportado
   */
  downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Listar execuções de relatórios
   */
  async listExecutions(filters?: ExecutionFilters): Promise<ReportExecutionListItem[]> {
    const response = await apiClient.get('/api/reports/executions', { params: filters });
    return response.data.data;
  }

  /**
   * Obter estatísticas do sistema de relatórios
   */
  async getStats(): Promise<ReportStatsResponse> {
    const response = await apiClient.get('/api/reports/stats');
    return response.data.data;
  }

  /**
   * Formatar nome de arquivo para download
   */
  getDownloadFilename(reportCode: string, format: string): string {
    const timestamp = new Date().getTime();
    const reportName = reportCode.replace(/_/g, '-');
    return `${reportName}-${timestamp}.${format}`;
  }
}

export interface ExecutionFilters {
  reportCode?: string;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface ReportExecutionListItem {
  id: string;
  reportCode: string;
  reportName?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  totalRecords?: number;
  durationMs?: number;
  executedByName: string;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface ReportStatsResponse {
  totalReports: number;
  executionsThisMonth: number;
  avgExecutionTime: number;
  totalStorageMB: number;
  cacheHitRate?: number;
  topReports?: Array<{
    code: string;
    name: string;
    executions: number;
    lastExecuted: string;
  }>;
}

export const reportsService = new ReportsService();
